import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ArrowRight, TrendingDown, Wallet, PiggyBank, ShieldCheck, Sliders, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Disclaimer } from "@/components/Disclaimer";
import { useApp } from "@/context/AppContext";
import { formatCompact, formatMonths, runwayDisplay, formatPercent } from "@/lib/currency";
import { getPricing, formatPrice } from "@/lib/pricing";
import { RunwayCalc } from "@/components/RunwayCalc";
import { ResilienceScore } from "@/components/ResilienceScore";
import { SaveReportCard } from "@/components/SaveReportCard";
import { exportReportPdf } from "@/lib/exportReportPdf";
import { track } from "@/lib/analytics";

function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const LOCKED_CARDS = [
  "Jika Gaji Suami Hilang",
  "Jika Gaji Istri Hilang",
  "Jika Beban Cicilan Naik",
  "Target Dana Darurat Ideal",
  "Batas Pengeluaran Aman",
  "Langkah Menuju Status Hijau",
];

function SnapshotRow({ label, value, sub, accent }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-zinc-600">{label}</p>
        {sub && <p className="text-xs text-zinc-400">{sub}</p>}
      </div>
      <span className={`font-mono text-base font-bold ${accent || "text-zinc-900"}`}>{value}</span>
    </div>
  );
}

export default function FreeResult() {
  const navigate = useNavigate();
  const { report } = useApp();
  const pricing = getPricing();

  const runwayTarget = report && !report.hero.after.isPositive ? report.hero.after.survivalRunway : 0;
  const animatedRunway = useCountUp(runwayTarget <= 24 ? runwayTarget : 0);

  useEffect(() => {
    if (report) track("report_viewed", { status: report.base.status });
  }, [report]);

  if (!report) return <Navigate to="/" replace />;

  // HARDENING: jangan pernah menampilkan skor untuk assessment serba-kosong
  // (pendapatan 0 DAN pengeluaran 0). Arahkan user mengulang pengisian.
  const d0 = report.data || {};
  const _totalIncome =
    (Number(d0.husbandSalary) || 0) +
    (Number(d0.wifeSalary) || 0) +
    (Array.isArray(d0.sideIncomes) ? d0.sideIncomes.reduce((s, x) => s + (Number(x && x.amount) || 0), 0) : 0);
  const _totalOutflow =
    (Number(d0.livingExpenses) || 0) +
    (Number(d0.mortgage) || 0) +
    (Number(d0.vehicle) || 0) +
    (Number(d0.creditCard) || 0) +
    (Number(d0.otherDebt) || 0);
  if (_totalIncome === 0 && _totalOutflow === 0) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-md lg:max-w-2xl flex-col items-center justify-center bg-white px-8 text-center" data-testid="result-incomplete">
        <ShieldCheck className="h-6 w-6 text-emerald-600" />
        <h1 className="mt-4 font-heading text-xl font-extrabold text-zinc-900">
          Data belum lengkap — ulangi pengisian
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-zinc-500">
          Kami tidak menemukan angka pemasukan maupun pengeluaran, sehingga skor tidak
          dapat dihitung secara jujur. Silakan ulangi pemeriksaan dengan data yang terisi.
        </p>
        <button
          data-testid="result-incomplete-retry"
          onClick={() => navigate("/assessment")}
          className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-semibold text-white hover:bg-zinc-800"
        >
          Ulangi Pengisian <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const { base, hero, sideStress } = report;
  const heroPositive = hero.after.isPositive;
  const lostLabel =
    hero.target === "husband"
      ? report.data.husbandName
        ? `gaji ${report.data.husbandName}`
        : "Gaji Suami"
      : report.data.wifeName
        ? `gaji ${report.data.wifeName}`
        : "Gaji Istri";
  const heroDisp = runwayDisplay(hero.after.survivalRunway);
  const heroAnimated = runwayTarget <= 24;

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-3xl bg-[#f8f8f7] pb-28">
      {/* Hero runway */}
      <section className="bg-zinc-900 px-6 pb-10 pt-12 text-white">
        <div className="mb-4 flex items-center gap-2 text-zinc-400">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-medium">{report.data.familyName || "Keluarga Anda"}</span>
        </div>

        {heroPositive ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 font-heading">
              Family Survival Runway
            </p>
            <h1 className="mt-2 font-heading text-3xl font-extrabold leading-tight">
              Cashflow keluarga tetap positif
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              Keluarga masih memiliki sisa sekitar{" "}
              <span className="font-mono font-semibold text-emerald-400">
                {formatCompact(hero.after.monthlyBalance)}
              </span>{" "}
              per bulan meskipun {lostLabel} berhenti.
            </p>
            <div className="mt-4">
              <RunwayCalc scenario={hero.after} lostLabel={lostLabel} dark showDefinition={false} />
            </div>
          </>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 font-heading">
              Family Survival Runway
            </p>
            <p className="mt-2 text-sm text-zinc-400">Keluarga Anda tahan sekitar</p>
            <div className="mt-1 flex items-baseline gap-2" data-testid="hero-runway">
              <span className="font-mono text-6xl font-black tracking-tighter text-white">
                {heroAnimated ? formatMonths(animatedRunway) : heroDisp.value}
              </span>
              <span className="font-heading text-2xl font-bold text-zinc-300">{heroDisp.unit}</span>
            </div>
            <p className="mt-3 text-sm text-zinc-400">
              jika {lostLabel} (penghasilan utama terbesar) berhenti hari ini dan pola
              pengeluaran tetap sama.
            </p>
            <div className="mt-4">
              <RunwayCalc scenario={hero.after} lostLabel={lostLabel} dark showDefinition={false} />
            </div>
          </>
        )}
      </section>

      <div className="space-y-4 px-6 pt-6">
        {/* Resilience score */}
        <ResilienceScore metrics={base} variant="card" />

        {/* Financial snapshot */}
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-400 font-heading">
            Pendapatan Keluarga
          </p>
          <p className="font-mono text-2xl font-black text-zinc-900">
            {formatCompact(base.totalFamilyIncome)}
          </p>
          <div className="mt-3 space-y-1 border-t border-zinc-100 pt-3">
            <SnapshotRow label="Gaji Utama" value={formatCompact(base.coreIncome)} />
            <div className="rounded-xl bg-emerald-50/70 px-3">
              <SnapshotRow
                label="Side Income"
                sub={`${formatPercent(base.sideDependency)}% dari total`}
                value={formatCompact(base.sideTotal)}
                accent="text-emerald-700"
              />
            </div>
          </div>

          <p className="mb-1 mt-6 text-xs font-semibold uppercase tracking-wider text-zinc-400 font-heading">
            Pengeluaran Wajib
          </p>
          <SnapshotRow label="Total Cicilan" value={formatCompact(base.totalDebt)} />
          <SnapshotRow label="Kebutuhan Rutin" value={formatCompact(base.living)} />

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-zinc-50 p-4">
              <div className="flex items-center gap-1.5 text-zinc-400">
                <PiggyBank className="h-3.5 w-3.5" />
                <span className="text-[11px] font-medium">Dana Darurat</span>
              </div>
              <p className="mt-1 font-mono text-lg font-bold text-zinc-900">
                {formatMonths(base.emergencyMonths)} bln
              </p>
              <p className="text-[11px] text-zinc-400">Target minimal 6 bulan</p>
            </div>
            <div className="rounded-2xl bg-zinc-50 p-4">
              <div className="flex items-center gap-1.5 text-zinc-400">
                <Wallet className="h-3.5 w-3.5" />
                <span className="text-[11px] font-medium">Surplus Bulanan</span>
              </div>
              <p
                className={`mt-1 font-mono text-lg font-bold ${
                  base.monthlyBalance >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                {base.monthlyBalance >= 0 ? "" : "-"}
                {formatCompact(Math.abs(base.monthlyBalance))}
              </p>
              <p className="text-[11px] text-zinc-400">per bulan</p>
            </div>
          </div>
        </div>

        {/* Free stress test — side income lost */}
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="free-stress-test">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
              <TrendingDown className="h-4 w-4 text-amber-600" />
            </div>
            <h3 className="font-heading text-lg font-bold text-zinc-900">Side Income Hilang</h3>
          </div>

          <div className="flex items-center justify-between rounded-2xl bg-zinc-50 p-4">
            <div className="text-center">
              <p className="text-[11px] text-zinc-400">Saat ini</p>
              <p className="font-mono text-sm font-bold text-zinc-900">
                {formatCompact(base.totalFamilyIncome)}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-zinc-300" />
            <div className="text-center">
              <p className="text-[11px] text-zinc-400">Tanpa side income</p>
              <p className="font-mono text-sm font-bold text-zinc-900">
                {formatCompact(sideStress.totalFamilyIncome)}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-zinc-500">Sisa / Defisit bulanan</span>
            <span
              className={`font-mono text-sm font-bold ${
                sideStress.isPositive ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {sideStress.isPositive
                ? formatCompact(sideStress.monthlyBalance)
                : "-" + formatCompact(sideStress.monthlyDeficit)}
            </span>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <StatusBadge status={base.status} />
            <ArrowRight className="h-4 w-4 text-zinc-300" />
            <StatusBadge status={sideStress.status} />
          </div>
        </div>

        {/* Free summary PDF (upgrade hook) */}
        <div
          className="flex items-center justify-between rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow"
          data-testid="free-pdf-card"
        >
          <div className="pr-3">
            <p className="font-heading text-sm font-bold text-zinc-900">Simpan ringkasan (PDF)</p>
            <p className="mt-1 text-xs text-zinc-500">
              Unduh ringkasan gratis untuk disimpan atau dibagikan ke keluarga.
            </p>
          </div>
          <Button
            data-testid="free-download-pdf-btn"
            onClick={() => exportReportPdf(report, [], { free: true })}
            variant="outline"
            className="h-10 shrink-0 rounded-full border-zinc-300 text-xs font-semibold"
          >
            <Download className="mr-1.5 h-4 w-4" /> Unduh
          </Button>
        </div>

        {/* Locked teaser: interactive side-income stress test (premium) */}
        {base.sideTotal > 0 && (
          <div
            className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow"
            data-testid="side-stress-teaser"
          >
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100">
                <Sliders className="h-4 w-4 text-zinc-700" />
              </div>
              <h3 className="font-heading text-lg font-bold text-zinc-900">
                Stress Test Penghasilan Tambahan
              </h3>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-zinc-500">
              Geser 0–100% untuk melihat skor, status, dan survival runway keluarga jika
              penghasilan tambahan berkurang sebagian atau hilang total.
            </p>

            {/* Non-interactive preview */}
            <div className="pointer-events-none select-none">
              <div className="mb-4 flex gap-2">
                {[25, 50, 75, 100].map((pct) => (
                  <div
                    key={pct}
                    className="flex-1 rounded-full bg-zinc-100 py-2 text-center text-sm font-semibold text-zinc-500"
                  >
                    -{pct}%
                  </div>
                ))}
              </div>
              <div className="relative h-2 w-full rounded-full bg-zinc-100">
                <div className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-zinc-300" />
                <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-zinc-900 shadow" />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 rounded-2xl bg-zinc-900 p-4 text-white">
                <div>
                  <p className="text-[10px] text-zinc-400">Skor</p>
                  <p className="font-mono text-sm font-bold">
                    {base.score} <span className="text-zinc-500">→</span>{" "}
                    <span className="blur-[3px]">6•</span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400">Status</p>
                  <p className="font-mono text-sm font-bold blur-[3px]">••••</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400">Runway</p>
                  <p className="font-mono text-sm font-bold blur-[3px]">•• bln</p>
                </div>
              </div>
            </div>

            {/* Unlock CTA */}
            <div className="mt-4 flex items-center justify-between rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3">
              <div className="flex items-center gap-2 text-zinc-600">
                <Lock className="h-4 w-4 text-zinc-400" />
                <span className="text-xs font-semibold">Simulasi interaktif</span>
              </div>
              <Button
                data-testid="side-stress-teaser-cta"
                onClick={() => {
                  track("premium_clicked");
                  navigate("/paywall");
                }}
                className="h-9 rounded-full bg-zinc-900 px-4 text-xs font-semibold hover:bg-zinc-800"
              >
                Buka
              </Button>
            </div>
          </div>
        )}

        {/* Premium teaser */}
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow">
          <h3 className="font-heading text-lg font-bold leading-snug text-zinc-900">
            Kami menemukan beberapa area yang dapat memperkuat ketahanan keluarga Anda.
          </h3>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {LOCKED_CARDS.map((c) => (
              <div
                key={c}
                className="relative overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-50 p-4"
              >
                <Lock className="mb-2 h-4 w-4 text-zinc-400" />
                <p className="text-xs font-semibold leading-tight text-zinc-600">{c}</p>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/70 to-transparent" />
              </div>
            ))}
          </div>
        </div>

        <Disclaimer className="pt-2" />
      </div>

      {/* Save report (optional cross-device) */}
      <div className="px-6 pt-4">
        <SaveReportCard nextPath="/result" />
      </div>

      {/* Sticky paywall CTA */}
      <div className="fixed bottom-0 left-1/2 w-full max-w-md lg:max-w-3xl -translate-x-1/2 border-t border-zinc-100 bg-white/95 p-4 backdrop-blur-lg">
        <div className="mb-2 flex items-center justify-center gap-2 text-sm">
          {pricing.isPromo && (
            <span className="text-zinc-400 line-through">{formatPrice(pricing.regularPrice)}</span>
          )}
          <span className="font-mono font-bold text-zinc-900">{formatPrice(pricing.price)}</span>
          {pricing.isPromo && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
              HARGA PELUNCURAN
            </span>
          )}
        </div>
        <Button
          data-testid="open-paywall"
          onClick={() => {
            track("premium_clicked");
            navigate("/paywall");
          }}
          className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
        >
          Buka Laporan Lengkap
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
